// ⌘K: one search for structures, views, scenarios and actions. While it's open, the structures
// that match are highlighted in 3D and everything else is ghosted.

import { useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { Clapperboard, Eye, RotateCcw, Search, Tag } from 'lucide-react';
import { useAtlas } from '../app/store.ts';
import { search, nearestNames } from '../data/search.ts';
import { groupLabel, hemisphereLabel, isContext, systemSwatch } from '../data/catalogue.ts';
import { SCENARIOS, scenarioById } from '../scenarios/index.ts';
import { offeredPresets } from '../app/scene.ts';
import { Kbd } from '../ui/primitives.tsx';

export function Palette() {
  const open = useAtlas((s) => s.paletteOpen);
  const setPalette = useAtlas((s) => s.setPalette);
  const catalogue = useAtlas((s) => s.catalogue);
  const scenario = useAtlas((s) => scenarioById(s.scenario));
  const packs = useAtlas((s) => s.engine.packs);
  const setQuery = useAtlas((s) => s.setQuery);
  const [q, setQ] = useState('');

  const pool = useMemo(() => catalogue?.structures.filter((s) => !isContext(s)) ?? [], [catalogue]);
  const hits = useMemo(() => search(pool, q, 24), [pool, q]);
  const suggestions = useMemo(() => (q && !hits.length ? nearestNames(pool, q) : []), [pool, q, hits.length]);

  useEffect(() => {
    if (!open) {
      setQ('');
      setQuery('', null);
    }
  }, [open, setQuery]);
  useEffect(() => {
    if (open) setQuery(q, q ? hits.map((h) => h.s.id) : null);
  }, [open, q, hits, setQuery]);

  const close = () => setPalette(false);
  const choose = (id: string) => {
    const s = useAtlas.getState();
    close();
    s.select(id);
    s.camera({ frame: { id } });
  };
  const run = (fn: () => void) => () => {
    close();
    fn();
  };
  const lq = q.toLowerCase();
  const views = offeredPresets(scenario, { packs }).filter((p) => !q || p.name.toLowerCase().includes(lq) || p.hint.toLowerCase().includes(lq));
  const scenes = SCENARIOS.filter((sc) => !q || sc.name.toLowerCase().includes(lq) || sc.tagline.toLowerCase().includes(lq));

  return (
    <Command.Dialog open={open} onOpenChange={setPalette} label="Search" className="palette" overlayClassName="palette-overlay" shouldFilter={false} loop data-label-exclude="">
      <div className="palette-input">
        <Search size={16} strokeWidth={1.5} />
        <Command.Input value={q} onValueChange={setQ} placeholder="Search structures, views and scenes" autoFocus />
        <Kbd>Esc</Kbd>
      </div>
      <Command.List className="palette-list">
        <Command.Empty className="palette-empty">
          {suggestions.length ? (
            <>
              <span>No match. Did you mean </span>
              {suggestions.map((s, i) => (
                <span key={s.id}>
                  <button type="button" className="link-btn" onClick={() => setQ(s.name)}>
                    {s.name}
                  </button>
                  {i < suggestions.length - 1 ? ', ' : '?'}
                </span>
              ))}
            </>
          ) : (
            'No results'
          )}
        </Command.Empty>
        {hits.length ? (
          <Command.Group heading="Structures">
            {hits.map(({ s, matched }) => (
              <Command.Item key={s.id} value={s.id} onSelect={() => choose(s.id)} onMouseEnter={() => useAtlas.getState().hover(s.id, 'ui')} onMouseLeave={() => useAtlas.getState().hover(null, 'ui')}>
                <span className="swatch" style={{ background: systemSwatch(s.system, s.color) }} aria-hidden="true" />
                <span className="item-main">
                  <span className="item-name">{s.name}</span>
                  {matched !== s.name ? <span className="item-sub">{matched}</span> : null}
                </span>
                <span className="item-meta">{[groupLabel(catalogue, s.system), hemisphereLabel(s.hemisphere)].filter(Boolean).join(' · ')}</span>
              </Command.Item>
            ))}
          </Command.Group>
        ) : null}
        {views.length ? (
          <Command.Group heading={`${scenario.name} views`}>
            {views.map((p) => (
              <Command.Item key={p.id} value={`view:${p.id}`} onSelect={run(() => useAtlas.getState().applyPreset(p))}>
                <Eye size={14} strokeWidth={1.5} />
                <span className="item-main">
                  <span className="item-name">{p.name}</span>
                </span>
                <span className="item-meta">{p.hint}</span>
              </Command.Item>
            ))}
          </Command.Group>
        ) : null}
        {scenes.length ? (
          <Command.Group heading="Scenes">
            {scenes.map((sc) => (
              <Command.Item key={sc.id} value={`scene:${sc.id}`} onSelect={run(() => useAtlas.getState().setScenario(sc.id))}>
                <Clapperboard size={14} strokeWidth={1.5} />
                <span className="item-main">
                  <span className="item-name">{sc.name}</span>
                </span>
                <span className="item-meta">
                  <Kbd>{sc.key}</Kbd>
                </span>
              </Command.Item>
            ))}
          </Command.Group>
        ) : null}
        {!q ? (
          <Command.Group heading="Actions">
            <Command.Item value="action:reset" onSelect={run(() => useAtlas.getState().resetView())}>
              <RotateCcw size={14} strokeWidth={1.5} />
              <span className="item-main">
                <span className="item-name">Reset the view</span>
              </span>
              <span className="item-meta">
                <Kbd>R</Kbd>
              </span>
            </Command.Item>
            <Command.Item value="action:labels" onSelect={run(() => useAtlas.getState().toggleLabels())}>
              <Tag size={14} strokeWidth={1.5} />
              <span className="item-main">
                <span className="item-name">Show or hide labels</span>
              </span>
              <span className="item-meta">
                <Kbd>L</Kbd>
              </span>
            </Command.Item>
          </Command.Group>
        ) : null}
      </Command.List>
    </Command.Dialog>
  );
}
